import { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Card,
  Combobox,
  GitHubIcon,
  Modal,
  TimeSeriesChart,
} from "@insights/ui";
import type { EngineeringHealthMetricId, EngineeringHealthSection } from "@insights/contracts";
import { useParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { MetricCard } from "../components/MetricCard";
import { formatEvidenceValue } from "../helpers/format-evidence-value";
import { formatHealthValue } from "../helpers/format-health-value";
import { formatIsoWeek } from "../helpers/format-iso-week";
import {
  normalizeHealthFilters,
  useEngineeringHealth,
  useEngineeringHealthArtifacts,
  type HealthFilters,
} from "../hooks/useEngineeringHealth";
import "../styles/engineering-health.css";

const defaults: HealthFilters = { period: 30, repository: "", teamId: "", contributor: "" };
const sectionCopy: Record<EngineeringHealthSection, { title: string; question: string }> = {
  flow: { title: "Flow", question: "Is engineering work moving through the system, or accumulating and waiting?" },
  feedback: { title: "Feedback", question: "How quickly and reliably does the engineering system report whether a change is healthy?" },
  delivery: { title: "Delivery", question: "How regularly do completed engineering changes reach an observable release boundary?" },
  behavior: { title: "Behavior", question: "How consistently does the cohort operate, and where are persistent differences worth understanding?" },
};

function seriesDescription(metric: EngineeringHealthMetricId) {
  if (metric === "open-pr-wip" || metric === "stale-prs") {
    return "Weekly snapshot at the end of each UTC calendar week.";
  }
  if (metric === "build-success" || metric === "review-concentration") {
    return "Weekly rate using observations from each UTC calendar week.";
  }
  return "Weekly median using observations from each UTC calendar week.";
}

function seriesReferenceLabel(metric: EngineeringHealthMetricId) {
  if (
    metric === "open-pr-wip"
    || metric === "stale-prs"
    || metric === "build-success"
    || metric === "review-concentration"
  ) {
    return "Overall";
  }

  return "Median";
}

export function EngineeringHealthPage({ section }: { section: EngineeringHealthSection }) {
  const { workspaceId } = useParams();
  const storageKey = `engineering-health-filters:${workspaceId}`;
  const [filters, setFilters] = useState<HealthFilters>(() => {
    try {
      return normalizeHealthFilters(
        JSON.parse(sessionStorage.getItem(storageKey) ?? "{}"),
      );
    } catch {
      return defaults;
    }
  });
  const [evidenceMetric, setEvidenceMetric] = useState<EngineeringHealthMetricId | null>(null);
  const [chartMetric, setChartMetric] = useState<EngineeringHealthMetricId | null>(null);
  const health = useEngineeringHealth(workspaceId, section, filters);
  const artifacts = useEngineeringHealthArtifacts(workspaceId, section, filters, evidenceMetric);
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [filters, storageKey]);
  const copy = sectionCopy[section];
  const chart = health.data?.metrics.find((item) => item.id === chartMetric) ?? null;
  const evidence = health.data?.metrics.find((item) => item.id === evidenceMetric) ?? null;
  const update = (next: Partial<HealthFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
  };
  return (
    <AppShell>
      <PageHeader
        eyebrow="Pulse"
        title={copy.title}
        description={copy.question}
      />
      <div className="health-filters" aria-label="Pulse filters">
        <Combobox
          label="Period"
          value={String(filters.period)}
          options={[
            { value: "30", label: "30 days" },
            { value: "90", label: "90 days" },
            { value: "180", label: "180 days" },
          ]}
          onValueChange={(value) => {
            update({ period: Number(value) as 30 | 90 | 180 });
          }}
        />
        <Combobox label="Repository" value={filters.repository || "all"} options={[{ value: "all", label: "All selected repositories" }, ...(health.data?.scopeOptions.repositories ?? []).map((value) => ({ value, label: value }))]} onValueChange={(value) => update({ repository: value === "all" ? "" : value })} />
        <Combobox label="Team" value={filters.teamId || "all"} options={[{ value: "all", label: "All teams" }, ...(health.data?.scopeOptions.teams ?? []).map((team) => ({ value: team.id, label: team.name }))]} onValueChange={(value) => update({ teamId: value === "all" ? "" : value, contributor: "" })} />
        <Combobox label="Contributor" value={filters.contributor || "all"} options={[{ value: "all", label: "All contributors" }, ...(health.data?.scopeOptions.contributors ?? []).map((value) => ({ value, label: `@${value}` }))]} onValueChange={(value) => update({ contributor: value === "all" ? "" : value })} />
      </div>
      {health.isLoading && <Loading />}
      {health.isError && (
        <p className="health-unavailable" role="alert">
          Unable to load Pulse data.
        </p>
      )}
      {health.data && <>
        {health.data.scopeNote && <p className="health-scope-note">{health.data.scopeNote}</p>}
        <div className="health-metric-grid">
          {health.data.metrics.map((item) => (
            <MetricCard
              key={item.id}
              metric={item}
              onChart={() => setChartMetric(item.id)}
              onInspect={() => setEvidenceMetric(item.id)}
            />
          ))}
        </div>
        <Card as="section" className="health-observations"><h2>Observations</h2><ul>{health.data.observations.map((observation, index) => <li key={`${observation.metric}:${index}`}>{observation.text}</li>)}</ul></Card>
        {health.data.outliers.length > 0 && <Card as="section" className="health-observations"><h2>Persistent differences</h2><ul>{health.data.outliers.map((outlier) => <li key={`${outlier.metric}:${outlier.identity}`}>@{outlier.identity}: {formatHealthValue(outlier.value, health.data!.metrics.find((item) => item.id === outlier.metric)!.unit)} against cohort {formatHealthValue(outlier.cohortValue, health.data!.metrics.find((item) => item.id === outlier.metric)!.unit)} ({outlier.sampleCount} samples)</li>)}</ul></Card>}
      </>}
      <Modal
        className="wide-modal"
        open={chart !== null}
        onOpenChange={(open) => {
          if (!open) {
            setChartMetric(null);
          }
        }}
        title={chart ? `${chart.label} over time` : "Metric over time"}
        description={chart ? seriesDescription(chart.id) : "Weekly metric observations."}
        headerAccessory={chart && (
          <strong
            className="health-evidence-aggregate"
            aria-label={`${chart.label}: ${formatHealthValue(chart.value, chart.unit)}`}
          >
            {formatHealthValue(chart.value, chart.unit)}
          </strong>
        )}
      >
        {chart && (
          <TimeSeriesChart
            ariaLabel={`${chart.label} by week`}
            data={chart.series.map((point) => ({
              label: formatIsoWeek(point.from),
              value: point.value,
              sampleCount: point.sampleCount,
            }))}
            formatValue={(value) => formatHealthValue(value, chart.unit)}
            {...(chart.value === null
              ? {}
              : { reference: {
                  label: seriesReferenceLabel(chart.id),
                  value: chart.value,
                } })}
          />
        )}
      </Modal>
      <Modal
        className="wide-modal health-evidence-modal"
        open={evidenceMetric !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEvidenceMetric(null);
          }
        }}
        title={(
          <span className="health-evidence-title">
            Underlying evidence
            {evidence && (
              <Badge>
                {evidence.sampleCount} {evidence.sampleCount === 1 ? "sample" : "samples"}
              </Badge>
            )}
          </span>
        )}
        description="Observable artifacts used for this metric."
        headerAccessory={evidence && (
          <strong
            className="health-evidence-aggregate"
            aria-label={`${evidence.label}: ${formatHealthValue(evidence.value, evidence.unit)}`}
          >
            {formatHealthValue(evidence.value, evidence.unit)}
          </strong>
        )}
      >
        {artifacts.isLoading && <Loading />}
        <div className="health-evidence">
          {artifacts.data?.items.map((item) => (
            <article key={item.id}>
              <div className="health-evidence-identity">
                <strong>{item.title}</strong>
                <span>
                  {item.repository}
                  {item.contributor ? ` · @${item.contributor}` : ""}
                </span>
              </div>
              {evidence && (
                <div className="health-evidence-measure">
                  <strong>{formatEvidenceValue(item, evidence)}</strong>
                  {item.detail && <span>{item.detail}</span>}
                </div>
              )}
              {item.url && (
                <ActionIcon
                  href={item.url}
                  icon={<GitHubIcon />}
                  label="Open on GitHub"
                  rel="noreferrer"
                  size="small"
                  target="_blank"
                />
              )}
            </article>
          ))}
        </div>
        {!artifacts.isLoading && !artifacts.data?.items.length && <p>No underlying artifacts are available for this scope.</p>}
      </Modal>
    </AppShell>
  );
}
